import axios from "axios";
import React from "react";

import { APIKey, FormSectionElements } from "../../entities/formTemplateDataType";
import classNames from 'classnames';
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import { submitForm } from "../../utilities/HtmlUtils";
import { displayField, getRootComponent } from "../../utilities/ElementUtils";
import Utils from "../../utilities/Utils";
import { calloutWidths, TOKEN_TYPE } from "../../entities/Constants";
import SessionStorageService from "../../services/SessionStorageService";
import { combineUrl, resolveApiUrl } from "../../utilities/URLUtils";
import { SpinnerComponent } from "../../commonComponent/SpinnerComponent";
import DataUtils from "../../utilities/DataUtils";
import { setBaseHeaders } from "../../utilities/submitUtils";
import { DynamicUrlInterpreter } from '../../utilities/DynamicUrlInterpreter';
import { getEnvVar, SCRIPT_ENV } from "../../config/RuntimeConfig";

interface Props {
  field: FormSectionElements;
  editable: boolean;
  title;
  labelControlArray;
  requiredField;
  callbackElement;
  callbackStepIndex?;
  isEng: boolean;
}

interface State {
  isBusy: boolean;
}

export class TextComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
       
    this.state = {
        isBusy: false
    }

    let field = this.props.field;
    field.IsDirty = false;
    FormValidationUtils.runInitJavascript(field);
    field.Visible = !field.InvisibleByDefault;
  }

  valueLength(value) {
    if (typeof value == "string") {
      return value.length;
    }
    return 0;
  }

  onTextEditChange = (e) => {
    //console.log('input change:', e.target.value);
    let field = this.props.field;
    let elem = e.originalEvent ? e.originalEvent.target : e.target;
    let val = elem.value;

    if (field.autoFormatPattern && typeof field.formatField === 'function') {
      const formatted = field.formatField(val, field.autoFormatPattern, field.Value);
      field.Value = formatted.slice(0, field.autoFormatPattern.length);
    } else {
      field.Value = val;
    }
    FormValidationUtils.markFieldDirty(field);
    FormValidationUtils.validateText(field);
    FormValidationUtils.runKeyStrokeJavascript(field);
    this.props.callbackElement(getRootComponent(field));
  };

  onBlur = (e) => {
    let field = this.props.field;
    FormValidationUtils.evalRegularExpression(field);

    if (field.Valid) {
      FormValidationUtils.runJavascript(field);
    }
    this.props.callbackElement(getRootComponent(field));
  };

  async componentDidMount(): Promise<void> {
    const field = this.props.field;
    const programContainer = getRootComponent(field);

    if (field.Api) {
      let api = resolveApiUrl(field.Api);
      
      api = DynamicUrlInterpreter.interpretUrl(api, {
        programContainer,
        appName: programContainer.CategoryId
      });

      const isExternalUrl = api.startsWith("http");

      if (!isExternalUrl) {
          api = programContainer.APIBasePath?.replace(/\/+$/, '') + '/' + api.replace(/^\/+/, '');
      }

      const urlWithoutProtocol = api.replace(/^https?:\/\//, '');
      if (urlWithoutProtocol.includes('//')) {
          console.log('[PagedSummaryList] Skipping request with unresolved placeholder:', api);
          return;
      }
      let response;
      const apiKey: APIKey = (programContainer.ApiKeyKey && programContainer.ApiKeyValue)
          ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue }
          : null;
        const token = !isExternalUrl
          ? (SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token)
          : null;
      const headers = setBaseHeaders('application/json', token, apiKey);
      const configs = isExternalUrl
          ? { headers: { 'Content-Type': 'application/json' } }
          : { headers, withCredentials: true };

      const fullUrl = combineUrl(programContainer.APIBasePath, api);
      this.setState({ isBusy: true });
      try {
        response = await axios.get(fullUrl, configs);
      } catch (ex) {
        console.log(ex);
        this.handleSubmitError();
        if (field?.EventScript) {
          const customJSFunc = eval(`(${field.EventScript})`);
          customJSFunc(field, SCRIPT_ENV);
        }
        this.props.callbackElement(programContainer);
        return;
      } finally {
        this.setState({ isBusy: false });
      }
      if (!response) {
        this.handleSubmitError();
        return;
      }

      programContainer.Entity = response.data;
      DataUtils.assignFormValue(programContainer);
      if (field?.EventScript) {
        const customJSFunc = eval(`(${field.EventScript})`);
        customJSFunc(field, SCRIPT_ENV);
      }
      this.props.callbackElement(programContainer);
    }
  }

  handleSubmitError = (response?: any) => {
    if (this.props.field.SubmitButton.apiErrorRedirectPage) {
      const sequence = Math.max(0, this.props.field.SubmitButton.apiErrorRedirectPage - 1);
      this.props.callbackStepIndex(sequence);
    }
  }

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>,
    snapshot?: any
  ): void {
    // if (prevProps.field.Value !== this.props.field.Value) {
      let field = this.props.field;
      // FormValidationUtils.evalRegularExpression(field);
   //   if (field.Valid) {
        // FormValidationUtils.runJavascript(field);
   //   }
    // }
  }

  handleKeyDown = (e) => {
    //console.log('key: ', e.key, e.keyCode);
    let field = this.props.field;
    if (e.key === "Enter" && field.input?.triggerSubmitOnEnter) {
      e.preventDefault();
      // FormValidationUtils.evalRegularExpression(field);
      if (this.props.field.Valid) {
        FormValidationUtils.runKeyStrokeJavascript(field);
        if (field.Valid) {
          submitForm(field);
        }
      }
    }
  };

  allowOnlyNumbers = (e) => {
    if (
      (e.key >= "0" && e.key <= "9") ||
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "Tab"
    ) {
      return;
    }
    e.preventDefault();
  };

  render() {
    let field = this.props.field;

    FormValidationUtils.runJavascript(field);
    const programContainer = getRootComponent(field);
    let displayfield: boolean = true;
    field.Visible = field.Visible ?? true;

    let editable = this.props.editable;
    let labelControlArray = this.props.labelControlArray;
    let requiredField = this.props.requiredField;
    let controlArray = [];
    let title = this.props.title;

    let isCounterDisplay = this.props.field.counterVisible;

    let lengthAndRow = this.props.field.Length;
    let numOfRow = 5;
    let maxLength = 500;

    if (lengthAndRow && lengthAndRow.indexOf(",") > 0) {
      let arr = lengthAndRow.split(",");
      numOfRow = parseInt(arr[0].toString(), 10);
      maxLength = parseInt(arr[1].toString(), 10);
    } else if (lengthAndRow) {
      maxLength = parseInt(lengthAndRow);
    }

    displayfield = displayField(field);

    const numArray = field.Length?.match(/\d+/g).map(Number);
    if (numArray && numArray.length > 0) {
      maxLength = numArray[0];
    }
    // the field script have high priority over th filter
    if (Utils.isNullOrEmpty(this.props.field.Script)) {
      this.props.field.Visible = displayfield;
    }

    if (field.maxLength) {
      maxLength = field.maxLength;
    }
    let hintHtml = this.props.isEng ? field.Hint : field.HintFR;

    const requiredLabel =
      this.props.field.Optional == "N"
        ? this.props.isEng
          ? "(required)"
          : "(obligatoire)"
        : this.props.isEng
        ? "(optional)"
        : "(facultatif)";

    const sectionReadOnly = field.parent?.parent?.SectionReadonly === true;
    field.ReadOnly = field.ReadOnly || sectionReadOnly;

    if (displayfield) {
      if (editable) {
        if (field.Value == null) field.Value = "";
        const validClass = field.Length
          ? "ontario-input" + " " + field.Length
          : "ontario-input";
        const invalidClass = field.Length
          ? "ontario-input" + " " + field.Length + " ontario-input__error"
          : "ontario-input ontario-input__error";

        controlArray.push(
          <div
            key={"div_" + field.Datatype + "_" + field.Id}
            id={"div_" + field.Datatype + "_" + field.Id}
            style={{
              textAlign: "left",
              display: `${field.inlineStyling ? "inline-block" : "block"}`,
              marginRight: "1rem",
            }}
          >
            <div key={"fdLbl_div_" + field.Id}>
              <label
                className="ontario-label"
                htmlFor={field.LoadBindName}
                id={`label_${field.LoadBindName}`}
                key={"fdLbl_" + field.Id}
                style={field?.fieldSize ? { fontSize: field?.fieldSize } : undefined}
              >
                {title}{" "}
                {programContainer.UseAsterisks === "Y" &&
                this.props.field.Optional === "N" ? (
                  <span style={{ color: "red" }}>*</span>
                ) : field.showOptionalLabel !== false && title ? (
                  <span className="ontario-label__flag">{requiredLabel}</span>
                ) : null}
                {field.TooltipENU && (
                  <i
                    role="tooltip"
                    title={field.TooltipENU}
                    className="pi pi-info-circle"
                  ></i>
                )}
              </label>
              {hintHtml ? (
                <p
                  className="ontario-hint"
                  id={`hint-text-${field.LoadBindName}`}
                  tabIndex={field.Valid ? undefined : -1}
                  style={field?.fieldHintSize ? { fontSize: field?.fieldHintSize } : undefined}
                  dangerouslySetInnerHTML={{ __html: hintHtml }}
                ></p>
              ) : null}
            </div>
            {!getRootComponent(field).ErrorBottom && (
              <LiveValidationErrMsg
                field={this.props.field}
                isEng={this.props.isEng}
                key={"LiveValidationErrMsg_" + field.LoadBindName}
                id={`${field.LoadBindName}-${field.Id}`}
              />
            )}
            {(!field.TextType || field.TextType === "Input") && (
              <input
                style={{
                  display: field.Visible ? "block" : "none",
                  color: field.ReadOnly
                    ? "color.palette.greyscale40"
                    : "color.palette.black",
                  ...(getRootComponent(field).ErrorBottom && {
                    marginBottom: "4px",
                  }),
                  ...(field.LengthInPixel && {
                    width: `${field.LengthInPixel}px`,
                  }), // Apply width dynamically
                }}
                type="text"
                id={field.LoadBindName}
                key={"fdIpt_" + field.LoadBindName}
                onBlur={this.onBlur}
                onKeyDownCapture={(event) => {
                  if (field.input && field.input.onlyNumbers) this.allowOnlyNumbers(event);
                }}
                className={field.Valid || !field.Touched ? validClass : invalidClass}
                maxLength={maxLength}
                value={field.Value}
                readOnly={field.ReadOnly}
                aria-invalid={!field.Valid  ? true : undefined}
                aria-describedby={
                  [
                    !field.Valid && `${field.LoadBindName}-${field.Id}-error`,
                    hintHtml && `hint-text-${field.LoadBindName}`,
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                aria-required={field.Optional !== 'Y' ? true : undefined}
                onChange={this.onTextEditChange}
                onKeyDown={this.handleKeyDown}
              />
            )}
            {field.informationalText && (
              <>
                <p style={{ maxWidth: calloutWidths.lg }}>
                  <svg
                    className="ontario-icon"
                    aria-hidden="true"
                    focusable="false"
                    viewBox="0 0 24 24"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <use href="#ontario-icon-alert-information"></use>
                  </svg>
                  {this.props.isEng
                    ? field.informationalText
                    : field.informationalTextFRA}
                </p>
              </>
            )}
            {getRootComponent(field).ErrorBottom && (
              <LiveValidationErrMsg
                field={this.props.field}
                isEng={this.props.isEng}
                key={"LiveValidationErrMsg_" + field.LoadBindName}
                id={`${field.LoadBindName}-${field.Id}`}
              />
            )}
            {field.TextType === "Value" && <p>{field.Value}</p>}
            <div
              style={{ display: isCounterDisplay ? "block" : "none" }}
              className="word-count"
            >
              {isCounterDisplay ? (
                this.valueLength(this.props.field.Value) + "/" + maxLength
              ) : (
                <br />
              )}
            </div>
          </div>
        );
      } else {
        controlArray.push(
          <div
            key={"div_" + field.Datatype + "_" + field.Id}
            className={
              programContainer.isHorizontalLayout
                ? "pdf-row-column"
                : "pdf-two-column"
            }
          >
            <div key={"div2_" + field.Datatype + "_" + field.Id}>{title}</div>
            <div
              key={"div3_" + field.Datatype + "_" + field.Id}
              className={classNames({
                "invalid-field--highlighted":
                  (this.props.field.Value == "" ||
                    this.props.field.Value == null) &&
                  requiredField,
              })}
            >
              {this.props.field.Value}
            </div>
          </div>
        );
      }
      controlArray.push(<div>
        <SpinnerComponent isBusy={this.state.isBusy && getEnvVar('IS_RUNTIME') != 'false'} />
      </div>);
    }

    return controlArray;
  }
}
